import { ApiError } from '@hq/api-sdk';
import { Button, Card, Spin, Typography } from 'antd';
import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { hq, useAuthStore } from '../session';

/**
 * 手机打开落地页：`/m-enter?ticket=…`（公开路由，手机浏览器扫码打开）。
 *
 * 电脑端「手机打开」生成的二维码指向这里。票据与当前电脑账号绑定、3 分钟有效、
 * 只能用一次，兑换成功即把同一登录态写入手机端会话并进入总览。
 * 手机浏览器有独立 sessionStorage，因此不会影响电脑端登录。
 */
export default function MobileEnter() {
  const [params] = useSearchParams();
  const ticket = (params.get('ticket') ?? '').trim();
  const navigate = useNavigate();
  const setSession = useAuthStore((state) => state.setSession);
  const [error, setError] = useState<string | null>(null);

  // StrictMode 下 effect 会跑两次：票据只能兑换一次，用 ref 守住
  const startedRef = useRef(false);

  useEffect(() => {
    if (startedRef.current) return;
    startedRef.current = true;

    const run = async () => {
      if (!ticket) {
        setError('链接缺少票据，请在电脑上重新生成二维码');
        return;
      }
      try {
        const result = await hq.auth.qrShare.redeem(ticket);
        setSession(result.token, {
          userId: String(result.profile.userId),
          orgId: String(result.profile.orgId),
          orgName: result.profile.orgName ?? null,
          deptId: String(result.profile.deptId),
          name: result.profile.realName ?? result.profile.userCode,
          level: result.profile.level ?? 'L4',
          perms: result.profile.perms ?? []
        });
        navigate('/overview', { replace: true });
      } catch (err) {
        // 这里优先用服务端原文：票据过期/已用/不存在各自的处置动作不同，
        // SDK 的统一 409 文案（「请刷新后查看结果」）会误导用户
        setError(err instanceof ApiError ? err.message || err.friendlyMessage : String(err));
      }
    };

    void run();
  }, [ticket, setSession, navigate]);

  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 16,
        background:
          'linear-gradient(135deg, var(--hq-color-primary-active, #6F0863) 0%, var(--hq-color-primary, #88157A) 55%, var(--hq-color-primary-hover, #AF4FA3) 100%)'
      }}
    >
      <Card style={{ width: 420, maxWidth: '100%', border: 'none' }} styles={{ body: { padding: 28 } }}>
        <div style={{ textAlign: 'center', marginBottom: 16 }}>
          <img src="/logo.png" alt="logo" style={{ width: 48, height: 48 }} />
          <Typography.Title level={4} style={{ margin: '12px 0 0' }}>
            手机打开
          </Typography.Title>
        </div>

        {error ? (
          <>
            <Typography.Paragraph type="danger" style={{ marginBottom: 4 }}>
              {error}
            </Typography.Paragraph>
            <Typography.Paragraph type="secondary" style={{ fontSize: 12 }}>
              二维码 3 分钟内有效且只能用一次；请回到电脑上点「手机打开」重新生成。
            </Typography.Paragraph>
            <Button block type="primary" style={{ marginTop: 8 }} onClick={() => navigate('/login', { replace: true })}>
              去登录页
            </Button>
            <Typography.Paragraph style={{ textAlign: 'center', margin: '12px 0 0', fontSize: 12 }}>
              <Link to="/login">手动输入账号登录</Link>
            </Typography.Paragraph>
          </>
        ) : (
          <div style={{ textAlign: 'center', padding: '12px 0' }}>
            <Spin />
            <Typography.Paragraph type="secondary" style={{ marginTop: 16, marginBottom: 0 }}>
              正在校验二维码并同步登录状态…
            </Typography.Paragraph>
          </div>
        )}
      </Card>
    </div>
  );
}
